import { computed, ref, watch, type Ref, type WritableComputedRef } from 'vue'

export interface UseControllableModelOptions<T> {
  defaultValue?: T | undefined
}

/**
 * Keeps controlled `v-model` props authoritative while giving unbound primitives a local value.
 * Writes always reach the model ref, so an uncontrolled interaction still emits
 * `update:modelValue` and can become controlled later without losing state.
 */
export function useControllableModel<T>(
  model: Ref<T | undefined>,
  options: UseControllableModelOptions<T> = {},
): WritableComputedRef<T | undefined> {
  const internal = ref<T | undefined>(options.defaultValue) as Ref<T | undefined>

  watch(model, (value) => {
    if (value !== undefined) internal.value = value
  })

  return computed({
    get: () => model.value ?? internal.value,
    set: (value) => {
      internal.value = value
      model.value = value
    },
  })
}
